import { useAuth } from "@clerk/nextjs";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { ToggleState } from "enums/toggles";
import useFilters from "features/FilterDrawer/hooks/useFilters";
import { getQueryFromFilters } from "features/FilterDrawer/utils/queryParams/filterQueryParams";
import useUserSettings from "features/User/hooks/useUserSettings";
import { PaperSearchResponse } from "helpers/api";
import { resultsPagePath } from "helpers/pageUrl";
import { getQueryFilterKeys, queryToResultPageParams } from "helpers/search";
import {
  MixpanelActionSource,
  MixpanelNavigationSource,
} from "helpers/services/mixpanel/events";
import useAnalytics from "hooks/useAnalytics";
import { useAppDispatch } from "hooks/useStore";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { focusSearchInput } from "store/slices/search";
import useProToggle, { PRO_ANALYSIS_QUERY_PARAM } from "./useProToggle";

export function useSearch() {
  const router = useRouter();
  const { isSignedIn, isLoaded } = useAuth();
  const { lang } = useUserSettings();
  const dispatch = useAppDispatch();
  const { searchEvent, setAnalyticNavigationSource } = useAnalytics();

  const queryClient = useQueryClient();
  const queryResultKey = ["results", ...getQueryFilterKeys(router.query)];
  const firstPageSearchId =
    queryClient.getQueryData<InfiniteData<PaperSearchResponse>>(queryResultKey)
      ?.pages[0]?.search_id;

  const { isProTogglePreferred } = useProToggle();

  const { appliedFilters } = useFilters();
  const filterParams = getQueryFromFilters(appliedFilters);

  const buildSearchQueryLink = useCallback(
    (value: string, { forceProAnalysisOn = false } = {}) => {
      const proAnalysisToggleState =
        forceProAnalysisOn || isProTogglePreferred ? ToggleState.ON : undefined;

      if (value) {
        return resultsPagePath(value, {
          ...queryToResultPageParams(router.query),
          [PRO_ANALYSIS_QUERY_PARAM]: proAnalysisToggleState,
          lang: lang,
          ...filterParams,
        });
      }
    },
    [isProTogglePreferred, lang, router, filterParams]
  );

  const dispatchSearchEvent = useCallback(
    (
      value: string,
      {
        navigationSource = MixpanelNavigationSource.Website,
        forceProAnalysisOn = false,
        actionSource = MixpanelActionSource.HomePageSuggestedSearchFeed,
        isRelatedSearch = false,
      }
    ) => {
      const proAnalysisToggleState =
        forceProAnalysisOn || isProTogglePreferred ? ToggleState.ON : undefined;

      // All searches are dispatched from the "Website" source by definition unless otherwise specified.
      setAnalyticNavigationSource(navigationSource);
      searchEvent({
        query: value,
        isProAnalysisOn: proAnalysisToggleState,
        isNewThread: true,
        actionSource,
        isRelatedSearch,
      });
    },
    [isProTogglePreferred, searchEvent, setAnalyticNavigationSource]
  );

  const focusSearchInputBox = useCallback((focus: boolean) => {
    dispatch(focusSearchInput(focus));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    buildSearchQueryLink,
    dispatchSearchEvent,
    authState: { isSignedIn, isLoaded },
    firstPageSearchId,
    queryResultKey,
    focusSearchInputBox,
  };
}
